<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">Receita</v-toolbar-title>
                    <v-spacer/>
                    <PeriodoFilter
                        v-model="periodo"
                        defaultPeriodo="Últimos 12 meses"
                        @update="consultar"
                    />
                </v-toolbar>

                <v-card elevation="2" class="ma-1">
                    <v-card-text>
                        <!-- Cards -->
                        <v-row>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`R$ ${formatPrice(totais.mrr)}`"
                                    label="MRR"
                                    color="cyan accent-4"
                                    icon="mdi-cash"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`R$ ${formatPrice(totais.arr)}`"
                                    label="ARR"
                                    color="light-green accent-4"
                                    icon="mdi-cash-multiple"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`R$ ${formatPrice(totais.total_faturado)}`"
                                    label="Faturamento no Periodo"
                                    color="blue darken-1"
                                    icon="mdi-chart-line"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`R$ ${formatPrice(totais.ticket_medio_geral)}`"
                                    label="Ticket Medio"
                                    color="amber lighten-1"
                                    icon="mdi-ticket-confirmation"
                                />
                            </v-col>
                        </v-row>

                        <!-- MRR por Plano -->
                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        MRR por Plano
                                    </div>
                                    <v-data-table
                                        :headers="headersMrrPlano"
                                        :items="mrrPorPlano"
                                        :mobileBreakpoint="0"
                                        hide-default-footer
                                        :items-per-page="-1"
                                        dense
                                    >
                                        <template v-slot:item.mrr="{ item }">
                                            R$ {{ formatPrice(item.mrr) }}
                                        </template>
                                    </v-data-table>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Faturamento Mensal -->
                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Faturamento Mensal
                                    </div>
                                    <v-card-text>
                                        <canvas ref="chartFaturamento" height="80"></canvas>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Ticket Medio Mensal -->
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Ticket Medio por Mes
                                    </div>
                                    <v-card-text>
                                        <canvas ref="chartTicket" height="120"></canvas>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Qtd. Pagamentos por Mes
                                    </div>
                                    <v-card-text>
                                        <canvas ref="chartPagamentos" height="120"></canvas>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Novo MRR -->
                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Novo MRR por Mes
                                    </div>
                                    <div class="subtitle-2 mb-2" style="text-align: center;">
                                        Total: R$ {{ formatPrice(novoMrrTotais.total_novo_mrr) }}
                                        | {{ novoMrrTotais.total_novos_clientes }} novos clientes
                                    </div>
                                    <v-card-text>
                                        <canvas ref="chartNovoMrr" height="80"></canvas>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Faturamento Detalhado -->
                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Faturamento Detalhado
                                    </div>
                                    <div class="subtitle-2 mb-2" style="text-align: center;">
                                        {{ faturamentoDetalhado.length }} pagamentos
                                        | Total: R$ {{ formatPrice(totais.total_faturado) }}
                                    </div>
                                    <v-data-table
                                        :headers="headersFaturamento"
                                        :items="faturamentoDetalhado"
                                        :mobileBreakpoint="0"
                                        :items-per-page="10"
                                        dense
                                    >
                                        <template v-slot:item.empresa_id="{ item }">
                                            <a @click.stop="$router.push(`/financeiro/${item.empresa_id}`)">
                                                {{ item.empresa_id }}
                                            </a>
                                        </template>
                                        <template v-slot:item.total_pago="{ item }">
                                            R$ {{ formatPrice(item.total_pago) }}
                                        </template>
                                        <template v-slot:item.paid_at="{ item }">
                                            {{ formatDate(item.paid_at) }}
                                        </template>
                                        <template v-slot:item.empresa_ativa="{ item }">
                                            <v-chip :color="item.empresa_ativa ? 'success' : 'error'" x-small>
                                                {{ item.empresa_ativa ? 'Ativo' : 'Inativo' }}
                                            </v-chip>
                                        </template>
                                    </v-data-table>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer.vue';
import Card from '@/views/home/Card.vue';
import PeriodoFilter from '@/components/PeriodoFilter.vue';
import Chart from 'chart.js';

export default {
    name: 'RelatorioReceita',
    components: { BaseContainer, Card, PeriodoFilter },

    data: () => ({
        periodo: {},
        loading: false,
        faturamentoMensal: [],
        faturamentoDetalhado: [],
        mrrData: {},
        mrrPorPlano: [],
        novoMrr: [],
        novoMrrTotais: { total_novo_mrr: 0, total_novos_clientes: 0 },
        totais: {
            mrr: 0, arr: 0, total_faturado: 0, ticket_medio_geral: 0, total_pagamentos: 0,
        },
        charts: {},
        headersMrrPlano: [
            { text: 'Plano', value: 'plano_nome' },
            { text: 'Clientes', value: 'qtd_clientes' },
            { text: 'MRR', value: 'mrr' },
        ],
        headersFaturamento: [
            { text: 'ID', value: 'empresa_id' },
            { text: 'Empresa', value: 'fantasia' },
            { text: 'Valor Pago', value: 'total_pago' },
            { text: 'Pago em', value: 'paid_at' },
            { text: 'Forma Pgto', value: 'forma_pagamento' },
            { text: 'Periodicidade', value: 'periodicidade' },
            { text: 'Status', value: 'empresa_ativa' },
        ],
    }),

    mounted() {
        this.$nextTick(() => this.consultar());
    },

    methods: {
        async consultar() {
            this.loading = true;
            const params = `start=${this.periodo.start || ''}&end=${this.periodo.end || ''}`;

            try {
                const [fatMensal, fatDetalhado, mrr, novoMrr] = await Promise.all([
                    this.$http.get(`relatorios/receita/faturamento-mensal?${params}`),
                    this.$http.get(`relatorios/receita/faturamento-detalhado?${params}`),
                    this.$http.get('relatorios/receita/mrr'),
                    this.$http.get(`relatorios/receita/novo-mrr?${params}`),
                ]);

                this.faturamentoMensal = fatMensal.data.data;
                this.faturamentoDetalhado = fatDetalhado.data.data;
                this.mrrData = mrr.data.data;
                this.mrrPorPlano = mrr.data.data.por_plano || [];
                this.novoMrr = novoMrr.data.data;
                this.novoMrrTotais = novoMrr.data.totais || {};

                this.totais = {
                    mrr: this.mrrData.mrr || 0,
                    arr: this.mrrData.arr || 0,
                    total_faturado: fatDetalhado.data.totais?.total_faturado || 0,
                    ticket_medio_geral: fatDetalhado.data.totais?.ticket_medio || 0,
                    total_pagamentos: fatDetalhado.data.totais?.qtd_pagamentos || 0,
                };

                this.gerarGraficos();
            } catch (e) {
                console.error(e);
                this.notify('Erro ao carregar relatório de receita', 'warning');
            }

            this.loading = false;
        },

        gerarGraficos() {
            this.renderChart('chartFaturamento', {
                type: 'bar',
                labels: this.faturamentoMensal.map(r => r.mes),
                data: this.faturamentoMensal.map(r => r.total),
                label: 'Faturamento (R$)',
                color: 'rgba(33, 150, 243, 0.7)',
                isCurrency: true,
            });

            this.renderChart('chartTicket', {
                type: 'line',
                labels: this.faturamentoMensal.map(r => r.mes),
                data: this.faturamentoMensal.map(r => r.ticket_medio),
                label: 'Ticket Medio (R$)',
                color: 'rgba(255, 193, 7, 0.8)',
                isCurrency: true,
            });

            this.renderChart('chartPagamentos', {
                type: 'bar',
                labels: this.faturamentoMensal.map(r => r.mes),
                data: this.faturamentoMensal.map(r => r.qtd_pagamentos),
                label: 'Pagamentos',
                color: 'rgba(76, 175, 80, 0.7)',
                isCurrency: false,
            });

            this.renderChart('chartNovoMrr', {
                type: 'bar',
                labels: this.novoMrr.map(r => r.mes),
                data: this.novoMrr.map(r => r.valor_total),
                label: 'Novo MRR (R$)',
                color: 'rgba(156, 39, 176, 0.7)',
                isCurrency: true,
            });
        },

        renderChart(ref, { type, labels, data, label, color, isCurrency }) {
            if (this.charts[ref]) this.charts[ref].destroy();
            const ctx = this.$refs[ref];
            if (!ctx) return;

            const currencyCallback = (value) => 'R$ ' + Number(value).toFixed(2);

            this.charts[ref] = new Chart(ctx, {
                type,
                data: {
                    labels,
                    datasets: [{
                        label,
                        data,
                        backgroundColor: color,
                        borderColor: color.replace('0.7', '1').replace('0.8', '1'),
                        borderWidth: 1,
                        fill: type === 'line',
                    }],
                },
                options: {
                    responsive: true,
                    scales: {
                        yAxes: [{
                            ticks: {
                                beginAtZero: true,
                                ...(isCurrency ? { callback: currencyCallback } : {}),
                            },
                        }],
                    },
                    tooltips: isCurrency ? {
                        callbacks: {
                            label: (tooltipItem, chartData) => {
                                const ds = chartData.datasets[tooltipItem.datasetIndex];
                                return ds.label + ': R$ ' + Number(tooltipItem.yLabel).toFixed(2);
                            },
                        },
                    } : {},
                },
            });
        },
    },
};
</script>
